/**
 * Staff calls · Meetings.
 *
 * A meeting is a group call with a time, an agenda and a list of invitees.
 * At its time somebody presses Start (the organiser whenever they like, an
 * invitee from ten minutes before); everyone invited is rung — except anybody
 * on Do not disturb, who still sees it here and can join — and it is live
 * until the last person leaves or a host ends it.
 *
 *  - Live now: group calls going on that I could come into — straight in, or
 *    by asking the host from the lobby.
 *  - Upcoming (live first) and Past, each with organiser, time and faces;
 *    the organiser edits or cancels one not yet started.
 *  - Schedule a meeting, or Meet now: the same form, with "now" for a time.
 *
 * Reminders come by themselves, above every screen (CallsProvider).
 */
import { useMemo, useState, type ReactNode } from "react";
import { useCallDirectory, useCallMeetings, useCancelMeeting, useLiveCalls, useSaveMeeting } from "../../api/queries";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Segmented,
  Select,
  Skeleton,
} from "../../design/components";
import {
  JOIN_MODE_LABEL,
  sortUpcoming,
  validateMeeting,
  type CallMedia,
  type JoinRule,
  type LiveCall,
  type Meeting,
  type MeetingDraft,
} from "../../domain/calls";
import { locale, t, tc, tn } from "../../i18n";
import type { CallsApi } from "./CallsProvider";
import { dndUntilText } from "./CallsMenu";
import { Face } from "./CallWindow";
import { JoinRuleEditor } from "./JoinRuleEditor";
import s from "./Calls.module.css";

const DURATIONS = [15, 30, 45, 60, 90, 120, 180];

const pad = (n: number) => String(n).padStart(2, "0");
const dateValue = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeValue = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

function nextHalfHour(now = new Date()): Date {
  const d = new Date(now);
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60);
  return d;
}

type FormMode = { kind: "new" } | { kind: "now" } | { kind: "edit"; meeting: Meeting };

export function MeetingsCard({ calls }: { calls: CallsApi }) {
  const [scope, setScope] = useState<"upcoming" | "past">("upcoming");
  const upcoming = useCallMeetings("upcoming");
  const past = useCallMeetings("past", scope === "past");
  const live = useLiveCalls();
  const cancel = useCancelMeeting();
  const [form, setForm] = useState<FormMode | null>(null);
  const busy = calls.state.busy || !!calls.state.call;
  const me = calls.state.me;

  const start = async (m: Meeting) => {
    const video = m.media === "video";
    if (m.state === "live" && m.callId) await calls.engine.join(m.callId, video);
    else await calls.engine.startMeeting(m.id, video);
    void upcoming.refetch();
    void live.refetch();
  };

  /* a meeting that is live also shows in Live now as its call: list it once, as the meeting */
  const meetingCalls = new Set((upcoming.data ?? []).map((m) => m.callId).filter(Boolean));
  /* nor the call I am on, or waiting to be let into */
  const liveCalls = (live.data ?? []).filter(
    (c) => !meetingCalls.has(c.id) && c.id !== calls.state.call?.id && c.id !== calls.state.waiting?.id,
  );

  return (
    <div id="meetings">
      <Card
        title="Meetings"
        sub="Scheduled group calls, with reminders 15 and 5 minutes before"
        testId="meetings-card"
        actions={
          <span className={s.cardActions}>
            <Button
              size="sm"
              variant="primary"
              disabled={busy}
              onClick={() => setForm({ kind: "now" })}
              data-testid="meet-now"
            >
              {t("Meet now")}
            </Button>
            <Button size="sm" onClick={() => setForm({ kind: "new" })} data-testid="schedule-open">
              {t("Schedule a meeting")}
            </Button>
          </span>
        }
      >
        {form && (
          <MeetingForm
            key={form.kind === "edit" ? form.meeting.id : form.kind}
            mode={form}
            calls={calls}
            close={() => setForm(null)}
          />
        )}

        {liveCalls.length > 0 && (
          <section className={s.mtSection} data-testid="live-calls">
            <h3 className={s.rtHead}>{t("Live calls you can join")}</h3>
            <ul className={s.meetings}>
              {liveCalls.map((c) => (
                <LiveRow key={c.id} c={c} calls={calls} busy={busy} />
              ))}
            </ul>
          </section>
        )}

        <div className={s.mtBar}>
          <Segmented
            value={scope}
            onChange={setScope}
            ariaLabel="Which meetings"
            options={[
              { value: "upcoming", label: "Upcoming" },
              { value: "past", label: "Past" },
            ]}
          />
        </div>

        {scope === "upcoming" ? (
          upcoming.isPending ? (
            <Skeleton rows={3} />
          ) : upcoming.isError ? (
            <ErrorState error={upcoming.error} retry={() => void upcoming.refetch()} />
          ) : !upcoming.data.length ? (
            <EmptyState>{t("No meetings coming up. Schedule one, or meet now.")}</EmptyState>
          ) : (
            <ul className={s.meetings} data-testid="meetings-upcoming">
              {sortUpcoming(upcoming.data).map((m) => (
                <MeetingRow
                  key={m.id}
                  m={m}
                  me={me}
                  busy={busy}
                  start={() => void start(m)}
                  edit={() => setForm({ kind: "edit", meeting: m })}
                  cancel={() => {
                    if (
                      window.confirm(t("Cancel “{title}”? Everyone invited will see it canceled.", { title: m.title }))
                    )
                      cancel.mutate(m.id);
                  }}
                />
              ))}
            </ul>
          )
        ) : past.isPending ? (
          <Skeleton rows={3} />
        ) : past.isError ? (
          <ErrorState error={past.error} retry={() => void past.refetch()} />
        ) : !past.data.length ? (
          <EmptyState>{t("No past meetings.")}</EmptyState>
        ) : (
          <ul className={s.meetings} data-testid="meetings-past">
            {past.data.map((m) => (
              <MeetingRow key={m.id} m={m} me={me} busy past />
            ))}
          </ul>
        )}
        {cancel.isError && <p className={s.refused}>{(cancel.error as Error).message}</p>}
      </Card>
    </div>
  );
}

function when(m: Meeting): string {
  const a = new Date(m.startsAt);
  const b = new Date(m.endsAt);
  const day = a.toLocaleDateString(locale(), { weekday: "short", day: "numeric", month: "short" });
  const f = (d: Date) => d.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" });
  return `${day} · ${f(a)}–${f(b)}`;
}

function MeetingRow({
  m,
  me,
  busy,
  past,
  start,
  edit,
  cancel,
}: {
  m: Meeting;
  me: string | undefined;
  busy: boolean;
  past?: boolean;
  start?: () => void;
  edit?: () => void;
  cancel?: () => void;
}) {
  const liveNow = m.state === "live";
  /* the platform lists a meeting nobody started, whose time has gone, as past */
  const notHeld = past && m.state === "scheduled";
  const organiser = m.organiser === me ? t("You") : (m.organiserName ?? "");
  const d = new Date(m.startsAt);
  return (
    <li className={s.meeting} data-state={m.state} data-testid={`meeting-${m.id}`}>
      <span className={s.mtDate} aria-hidden="true">
        <b>{d.toLocaleDateString(locale(), { day: "numeric" })}</b>
        <span>{d.toLocaleDateString(locale(), { month: "short" })}</span>
      </span>
      <div className={s.mtWho}>
        <b>
          {m.title}
          {liveNow && <span className={s.liveBadge}>{tc("Live", "call")}</span>}
          {m.state === "cancelled" && <span className={s.mutedBadge}>{t("Canceled")}</span>}
          {notHeld && <span className={s.mutedBadge}>{t("Not held")}</span>}
          {m.sample && <span className={s.sampleTag}>{t("Sample")}</span>}
        </b>
        <span className={s.dim}>
          {when(m)} · {m.media === "video" ? t("Video") : t("Voice")} · {t("Organiser: {name}", { name: organiser })} ·{" "}
          {t(JOIN_MODE_LABEL[m.join.mode])}
        </span>
        {m.agenda && <span className={s.mtAgenda}>{m.agenda}</span>}
        <span className={s.mtFaces}>
          {m.invitees.slice(0, 7).map((i) => (
            <span key={i.principalId} title={i.name}>
              <Face name={i.name} employeeId={i.employeeId} hasPhoto={i.hasPhoto} size={26} />
            </span>
          ))}
          {m.invitees.length > 7 && <span className={s.dim}>+{m.invitees.length - 7}</span>}
          <span className={s.dim}>{tn(m.invitees.length, "{n} invited", "{n} invited")}</span>
        </span>
      </div>
      {!past && (
        <div className={s.mtButtons}>
          {(liveNow || m.canStart) && start && (
            <Button
              size="sm"
              variant="primary"
              disabled={busy}
              onClick={start}
              title={busy ? t("Finish the call you are on first.") : undefined}
              data-testid={`meeting-go-${m.id}`}
            >
              {liveNow ? t("Join") : t("Start")}
            </Button>
          )}
          {m.canEdit && edit && (
            <Button size="sm" onClick={edit} data-testid={`meeting-edit-${m.id}`}>
              {t("Edit")}
            </Button>
          )}
          {m.canEdit && cancel && (
            <Button size="sm" variant="ghost" onClick={cancel} data-testid={`meeting-cancel-${m.id}`}>
              {t("Cancel meeting")}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

function LiveRow({ c, calls, busy }: { c: LiveCall; calls: CallsApi; busy: boolean }) {
  const on = c.participants.filter((p) => p.state === "joined");
  const host = c.participants.find((p) => p.principalId === (c.host ?? c.startedBy));
  return (
    <li className={s.meeting} data-state="live" data-testid={`live-${c.id}`}>
      <span className={`${s.mtDate} ${s.mtLive}`} aria-hidden="true">
        <b>●</b>
        <span>{tc("Live", "call")}</span>
      </span>
      <div className={s.mtWho}>
        <b>
          {c.title || host?.name || t("A group call")}
          {c.sample && <span className={s.sampleTag}>{t("Sample")}</span>}
        </b>
        <span className={s.dim}>
          {host && <>{t("Host: {name}", { name: host.name })} · </>}
          {tn(on.length, "{n} on the call", "{n} on the call")} · {c.media === "video" ? t("Video") : t("Voice")}
        </span>
        <span className={s.mtFaces}>
          {on.slice(0, 7).map((p) => (
            <span key={p.principalId} title={p.name}>
              <Face name={p.name} employeeId={p.employeeId} hasPhoto={p.hasPhoto} size={26} />
            </span>
          ))}
        </span>
      </div>
      <div className={s.mtButtons}>
        <Button
          size="sm"
          variant={c.can === "join" ? "primary" : "default"}
          disabled={busy || !!calls.state.waiting}
          onClick={() => void calls.engine.join(c.id, c.media === "video")}
          data-testid={`live-join-${c.id}`}
        >
          {c.can === "join" ? t("Join") : t("Ask to join")}
        </Button>
      </div>
    </li>
  );
}

/* ── the form ─────────────────────────────────────────────────────────── */

/** A labelled group of controls. Not a <label>: a label wrapping buttons presses the first one when its words are clicked. */
function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={s.group2} role="group" aria-label={t(label)}>
      <span className={s.group2Label}>{t(label)}</span>
      {children}
    </div>
  );
}

function MeetingForm({ mode, calls, close }: { mode: FormMode; calls: CallsApi; close: () => void }) {
  const dir = useCallDirectory();
  const save = useSaveMeeting();
  const editing = mode.kind === "edit" ? mode.meeting : undefined;
  const now = mode.kind === "now";
  const first = editing ? new Date(editing.startsAt) : nextHalfHour();
  const [title, setTitle] = useState(editing?.title ?? (now ? t("Quick meeting") : ""));
  const [agenda, setAgenda] = useState(editing?.agenda ?? "");
  const [date, setDate] = useState(dateValue(first));
  const [time, setTime] = useState(timeValue(first));
  const [duration, setDuration] = useState(editing?.durationMin ?? 30);
  const [media, setMedia] = useState<CallMedia>(editing?.media ?? "video");
  const [invitees, setInvitees] = useState<string[]>(editing?.invitees.map((i) => i.principalId) ?? []);
  const [join, setJoin] = useState<JoinRule>(editing?.join ?? { mode: "invited", allow: [] });
  const [q, setQ] = useState("");
  const [problems, setProblems] = useState<string[]>([]);
  const summary = dir.data?.summary;
  const groupMax = summary?.groupMax ?? 6;
  const people = useMemo(
    () =>
      (dir.data?.items ?? [])
        .filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [dir.data, q],
  );

  const submit = async () => {
    const startsAt = now ? new Date().toISOString() : new Date(`${date}T${time}`).toISOString();
    const draft: MeetingDraft = { title, agenda, startsAt, durationMin: duration, invitees, join, media };
    const out = validateMeeting(draft, groupMax, Date.now());
    if (summary && !summary.canGroup && invitees.length > 1) out.push("Your level may not start group calls.");
    setProblems(out);
    if (out.length) return;
    try {
      const m = await save.mutateAsync({ draft, id: editing?.id });
      if (now) await calls.engine.startMeeting(m.id, media === "video");
      close();
    } catch {
      /* the platform's refusal is shown below */
    }
  };

  return (
    <div className={s.mtForm} data-testid="meeting-form">
      <div className={s.mtFormHead}>
        <b>{editing ? t("Edit the meeting") : now ? t("Meet now") : t("Schedule a meeting")}</b>
        {now && <span className={s.dim}>{t("Starts as soon as you press Start: everyone you choose is rung.")}</span>}
      </div>
      <div className={s.mtFields}>
        <Field label="Title">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={160}
            placeholder={t("e.g. Apron safety walk — briefing")}
            data-testid="mt-title"
          />
        </Field>
        {!now && (
          <>
            <Field label="Date">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="mt-date" />
            </Field>
            <Field label="Time">
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="mt-time" />
            </Field>
          </>
        )}
        <Field label="Duration">
          <Select value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="mt-duration">
            {DURATIONS.map((d) => (
              <option key={d} value={d}>
                {d < 60
                  ? t("{n} min", { n: d })
                  : d % 60
                    ? t("{h} h {m} min", { h: Math.floor(d / 60), m: d % 60 })
                    : t("{h} h", { h: d / 60 })}
              </option>
            ))}
          </Select>
        </Field>
        <Group label="Call">
          <Segmented
            value={media}
            onChange={setMedia}
            ariaLabel="Voice or video"
            options={[
              { value: "video", label: "Video" },
              { value: "audio", label: "Voice" },
            ]}
          />
        </Group>
      </div>
      {!now && (
        <Field label="Agenda">
          <textarea
            className={s.textarea}
            value={agenda}
            onChange={(e) => setAgenda(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder={t("What the meeting is for, in a line or two")}
            data-testid="mt-agenda"
          />
        </Field>
      )}

      <div className={s.mtPick}>
        <div className={s.mtPickHead}>
          <b>{t("Invite")}</b>
          <span className={s.dim}>
            {t("{n} chosen · a call holds up to {max} people, you included", { n: invitees.length, max: groupMax })}
          </span>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Search a name")}
            aria-label={t("Search a name")}
            className={s.mtSearch}
          />
        </div>
        <ul className={s.mtPeople} data-testid="mt-invitees">
          {people.map((p) => {
            const on = invitees.includes(p.principalId);
            const full = !on && invitees.length + 1 >= groupMax;
            return (
              <li key={p.principalId}>
                <label title={p.canCall ? undefined : p.reason ? t(p.reason) : t("Not allowed")}>
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={!p.canCall || full}
                    onChange={() =>
                      setInvitees((x) => (on ? x.filter((y) => y !== p.principalId) : [...x, p.principalId]))
                    }
                    data-testid={`mt-invite-${p.principalId}`}
                  />
                  <Face name={p.name} employeeId={p.employeeId} hasPhoto={p.hasPhoto} size={26} />
                  <span>{p.name}</span>
                  {p.dnd && (
                    <em className={s.dndChip}>
                      ☾ {t("Do not disturb")} {dndUntilText(p.dndUntil, !p.dndUntil)}
                    </em>
                  )}
                  {!p.canCall && <em className={s.dim}>{p.reason ? t(p.reason) : t("Not allowed")}</em>}
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      <Group label="Who can join">
        <JoinRuleEditor value={join} onChange={setJoin} testId="mt-join" />
      </Group>

      {(problems.length > 0 || save.isError) && (
        <ul className={s.problems} role="alert">
          {problems.map((p) => (
            <li key={p}>{t(p)}</li>
          ))}
          {save.isError && <li>{(save.error as Error).message}</li>}
        </ul>
      )}
      <div className={s.saveBar}>
        <Button
          variant="primary"
          disabled={save.isPending || calls.state.busy}
          onClick={() => void submit()}
          data-testid="mt-save"
        >
          {save.isPending ? t("Saving…") : editing ? t("Save changes") : now ? t("Start") : t("Schedule")}
        </Button>
        <Button variant="ghost" onClick={close}>
          {t("Close")}
        </Button>
      </div>
    </div>
  );
}
